import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsService } from '../core/analytics.service';

interface QuotePackage {
  key:string;
  label:string;
  audience:string;
  setup:number;
  monthly:number;
  yearly:number;
  recommended?:boolean;
  unitPrice?:number;
  unitNote?:string;
  included?:string[];
}

interface QuoteData {
  quoteID:number;
  reference:string;
  status:string;
  expired:boolean;
  validUntil:string;
  customer:{name?:string;organization?:string};
  request:string;
  country:string;
  currency:string;
  included:string[];
  outcomes:string[];
  phases:string[];
  assumptions:string[];
  risks:string[];
  questions:string[];
  packages:QuotePackage[];
  recommendedPackage:string;
  commercialModel:'one_time'|'subscription'|'hybrid'|'phased'|'discovery';
  offerMode:'fixed'|'configured'|'phased'|'range';
  priceRange:{minimum?:number;maximum?:number};
  commercialSummary:string;
  breakdown:Array<{key:string;label:string;quantity:number;oneTime:number;monthly:number}>;
  pricingBasis:string;
  note:string;
  purchase?:{
    purchaseID:number;
    purchaseToken:string;
    packageKey:string;
    billingCycle:'monthly'|'yearly';
    dueNow:number;
    buyerName?:string;
    buyerEmail?:string;
    organization?:string;
    taxCountry?:string;
    taxNumber?:string;
    billingAddress?:string;
    paymentMethod?:'credit_card'|'bank_transfer';
    status:string;
    reference:string;
  }|null;
}

@Component({
  selector: 'app-aymudo-quote-page',
  imports: [FormsModule],
  template: `
    <main class="quote-shell">
      @if (loading()) {
        <section class="state"><i></i><strong>Teklifin hazırlanıyor</strong><span>Kapsam ve fiyat bütünlüğü doğrulanıyor.</span></section>
      } @else if (error()) {
        <section class="state error"><strong>Bu teklif açılamadı.</strong><span>{{ error() }}</span><a href="/">Aymudo'ya dön</a></section>
      } @else if (quote(); as data) {
        <header class="quote-header">
          <a href="/" class="brand">AYMUDO</a>
          <span>{{ data.reference }}</span>
          <small>{{ dateText(data.validUntil) }}</small>
        </header>

        @if (stage() === 'done') {
          <section class="complete">
            <div class="check">✓</div>
            <p>SATIN ALMA KAYDIN OLUŞTU</p>
            <h1>Birlikte başlıyoruz.</h1>
            <strong>{{ purchaseReference() }}</strong>
            <span>{{ successMessage() }}</span>
            <a href="mailto:offer@aymudo.com">Ekibimizle iletişime geç</a>
          </section>
        } @else {
          <section class="intro" data-analytics-section="quote-intro" data-analytics-label="Teklif özeti">
            <div>
              <p>İHTİYACINA ÖZEL TEKLİF</p>
              <h1>{{ data.customer.organization || data.customer.name || 'Projen' }} için büyüme planı.</h1>
            </div>
            <aside><span>Talebin</span><p>{{ data.request }}</p></aside>
          </section>

          <section class="scope" data-analytics-section="quote-scope" data-analytics-label="Teklif kapsamı">
            <div><p>KURULACAK YAPI</p><h2>Talebindeki operasyona göre hazırlanan kapsam.</h2></div>
            <ul>@for (item of data.included; track item) { <li><i>✓</i>{{ item }}</li> }</ul>
          </section>

          @if (data.outcomes?.length || data.phases?.length) {
            <section class="delivery" data-analytics-section="quote-delivery" data-analytics-label="İş sonuçları ve teslimat">
              @if (data.outcomes?.length) {
                <div class="outcomes"><p>HEDEFLEDİĞİMİZ SONUÇ</p><h2>Yazılım değil, çalışan bir operasyon.</h2><ul>@for (item of data.outcomes; track item) { <li>{{ item }}</li> }</ul></div>
              }
              @if (data.phases?.length) {
                <div class="phases"><p>UYGULAMA PLANI</p>@for (item of data.phases; track item; let index=$index) { <article><b>{{ index + 1 }}</b><span>{{ item }}</span></article> }</div>
              }
            </section>
          }

          @if (data.questions.length) {
            <section class="questions" data-analytics-section="quote-questions" data-analytics-label="Kapsam soruları">
              <header><div><p>3 KISA SORU</p><h2>Teklifi sana daha da yaklaştıralım.</h2></div>@if (answersSaved()) { <b>Yanıtlar kaydedildi</b> }</header>
              <div class="question-grid">
                @for (question of data.questions; track question; let index = $index) {
                  <label><span>{{ index + 1 }}. {{ question }}</span><textarea [(ngModel)]="answers[index]" rows="3" placeholder="Kısa yanıtın"></textarea></label>
                }
              </div>
              <button class="ghost" type="button" (click)="saveAnswers()" [disabled]="working()">Yanıtları teklife ekle</button>
            </section>
          }

          <section class="pricing" data-analytics-section="quote-pricing" data-analytics-label="Paket ve fiyatlar">
            <header>
              <div><p>{{ data.offerMode === 'range' ? 'ÖN DEĞERLENDİRME' : 'TİCARİ ÇERÇEVE' }}</p><h2>{{ data.commercialSummary || 'İşine en doğru yerden başla.' }}</h2></div>
              @if (hasRecurring(data)) {
                <div class="cycle"><button [class.active]="billingCycle()==='monthly'" (click)="billingCycle.set('monthly')">Aylık</button><button [class.active]="billingCycle()==='yearly'" (click)="billingCycle.set('yearly')">Yıllık <small>2 ay avantaj</small></button></div>
              }
            </header>
            @if (data.offerMode === 'range') {
              <article class="range-card">
                <span>İlk yatırım aralığı</span>
                <strong>{{ money(data.priceRange.minimum || 0, data.currency) }} – {{ money(data.priceRange.maximum || 0, data.currency) }}</strong>
                <p>Bu rakam kesin teklif değildir. Yukarıdaki kapsam sorularına vereceğin yanıtlarla gereksiz işleri çıkarıp doğru ticari modeli hesaplayacağız.</p>
              </article>
            } @else {
              <div class="package-grid">
                @for (item of data.packages; track item.key) {
                  <article [class.recommended]="item.recommended" [class.selected]="selectedPackage()===item.key">
                    @if (item.recommended) { <em>İHTİYACINA GÖRE ÖNERİLEN</em> }
                    <span>{{ item.audience }}</span>
                    <h3>{{ item.label }}</h3>
                    <div class="setup">
                      <small>{{ data.commercialModel === 'one_time' ? 'Proje bedeli' : 'Kurulum ve yayına alma' }}</small>
                      @if(item.setup > 0){
                        <strong>{{ money(item.setup, data.currency) }}</strong><i>tek sefer</i>
                      } @else {
                        <strong>Kurulum dahil</strong><i>ayrı kurulum bedeli yok</i>
                      }
                    </div>
                    @if (item.monthly > 0) {
                      <div class="recurring"><strong>{{ money(billingCycle()==='yearly' ? item.yearly : item.monthly, data.currency) }}</strong><span>/ {{ billingCycle()==='yearly' ? 'yıl' : 'ay' }}</span></div>
                    } @else {
                      <div class="no-recurring">Zorunlu aylık ücret yok</div>
                    }
                    @if (item.unitPrice) { <small>+ {{ money(item.unitPrice, data.currency) }} · {{ item.unitNote || 'ölçek kullanım bedeli' }}</small> }
                    <ul>@for (feature of item.included || []; track feature) { <li>{{ feature }}</li> }</ul>
                    <button type="button" data-analytics-action="quote.package" [attr.data-analytics-label]="item.label" (click)="selectPackage(item.key)">{{ selectedPackage()===item.key ? 'Seçildi' : 'Bu çözümü seç' }}</button>
                  </article>
                }
              </div>
            }
            @if (data.pricingBasis) { <div class="pricing-basis"><strong>Fiyat neye dayanıyor?</strong><span>{{ data.pricingBasis }}</span></div> }
            <p class="note">{{ data.note }}</p>
          </section>

          @if (data.assumptions?.length) {
            <section class="assumptions" data-analytics-section="quote-assumptions" data-analytics-label="Teklif varsayımları">
              <p>VARSAYIMLAR VE HARİÇ TUTULANLAR</p>
              <ul>@for (item of data.assumptions; track item) { <li>{{ item }}</li> }</ul>
            </section>
          }

          @if (selectedPackage()) {
            <section class="purchase" id="purchase" data-analytics-section="quote-purchase" data-analytics-label="Satın alma adımı">
              @if (stage() === 'select') {
                <div><p>SEÇİMİN HAZIR</p><h2>{{ selected()?.label }} ile başlayalım.</h2><span>Bugün oluşturulacak satın alma kaydı: kurulum + ilk {{ billingCycle()==='yearly' ? 'yıl' : 'ay' }}.</span></div>
                <aside><strong>{{ money(dueNow(), data.currency) }}</strong><small>ödenecek başlangıç toplamı</small><button (click)="accept()" [disabled]="working()">Teklifi kabul et</button></aside>
              } @else if (stage() === 'billing') {
                <div class="billing-copy"><p>SON ADIM</p><h2>Fatura ve ödeme tercihin.</h2><span>Bu bilgiler yalnız satın alma ve sözleşme hazırlığı için kullanılır.</span></div>
                <form (submit)="purchase($event)">
                  <label><span>Yetkili adı soyadı</span><input name="buyerName" [(ngModel)]="buyerName" required /></label>
                  <label><span>E-posta</span><input name="buyerEmail" [(ngModel)]="buyerEmail" type="email" required /></label>
                  <label><span>Şirket / işletme</span><input name="organization" [(ngModel)]="organization" required /></label>
                  <label><span>Ülke</span><input name="taxCountry" [(ngModel)]="taxCountry" maxlength="2" placeholder="TR" required /></label>
                  <label><span>Vergi numarası <small>opsiyonel</small></span><input name="taxNumber" [(ngModel)]="taxNumber" /></label>
                  <label class="wide"><span>Fatura adresi</span><textarea name="billingAddress" [(ngModel)]="billingAddress" rows="3" required></textarea></label>
                  <div class="payment wide">
                    <button type="button" [class.active]="paymentMethod==='credit_card'" (click)="paymentMethod='credit_card'"><b>Kartla ödeme</b><small>Güvenli ödeme bağlantısı</small></button>
                    <button type="button" [class.active]="paymentMethod==='bank_transfer'" (click)="paymentMethod='bank_transfer'"><b>Banka transferi</b><small>Kurumsal ödeme bilgileri</small></button>
                  </div>
                  @if (actionError()) { <p class="form-error wide">{{ actionError() }}</p> }
                  <button class="submit wide" type="submit" [disabled]="working()">{{ working() ? 'Kaydediliyor…' : 'Satın almayı tamamla' }}</button>
                </form>
              }
            </section>
          }

          <section class="decision-exit" data-analytics-section="quote-feedback" data-analytics-label="Teklif geri bildirimi">
            @if (!declined()) {
              <button type="button" class="exit-link" data-analytics-action="cta.click" data-analytics-label="Teklif geri bildirimini aç" (click)="declineOpen.set(!declineOpen())">Şimdilik ilerlemeyeceğim</button>
              @if (declineOpen()) {
                <form (submit)="decline($event)">
                  <label><span>Seni durduran temel neden nedir?</span>
                    <select name="declineReason" [(ngModel)]="declineReason">
                      <option value="price">Fiyat bütçemin üzerinde</option>
                      <option value="timing">Şu an doğru zaman değil</option>
                      <option value="missing_feature">İhtiyacım olan bir özellik eksik</option>
                      <option value="decision_maker">Kararı başka biriyle vermeliyim</option>
                      <option value="competitor">Başka bir çözüm seçtim</option>
                      <option value="trust">Daha fazla güven veya referans gerekli</option>
                      <option value="other">Başka bir neden</option>
                    </select>
                  </label>
                  <label><span>İstersen kısa bir not bırak</span><textarea name="declineNote" [(ngModel)]="declineNote" rows="2"></textarea></label>
                  <button type="submit" [disabled]="working()">Geri bildirimi gönder</button>
                </form>
              }
            } @else {
              <p>Teşekkürler. Geri bildirimin bir sonraki iyileştirmeyi belirlememize yardımcı olacak.</p>
            }
          </section>
        }
      }
    </main>
  `,
  styles: [`
    .delivery,.assumptions{border-top:1px solid #29433d;padding:55px 0}.delivery{display:grid;gap:36px;grid-template-columns:1fr 1fr}.delivery p,.assumptions>p{color:#ff765f;font-size:.75rem;font-weight:950;letter-spacing:.18em}.delivery h2{font:700 clamp(2rem,4vw,4rem)/1 Georgia,serif;margin:10px 0 28px}.outcomes ul,.assumptions ul{color:#b6c9c4;display:grid;gap:13px;line-height:1.55;padding-left:20px}.phases{display:grid;gap:10px}.phases article{align-items:center;background:#10201d;border:1px solid #29433d;border-radius:18px;display:grid;gap:14px;grid-template-columns:40px 1fr;padding:14px}.phases b{align-items:center;background:#6ee7d8;border-radius:50%;color:#071311;display:flex;height:34px;justify-content:center;width:34px}.pricing-basis{background:#10201d;border:1px solid #29433d;border-radius:18px;display:grid;gap:7px;margin-top:22px;padding:17px}.pricing-basis span{color:#b6c9c4;line-height:1.55}.assumptions{color:#8fa9a2}.range-card{background:linear-gradient(135deg,#10201d,#142b27);border:1px solid #3c5b54;border-radius:28px;display:grid;gap:16px;padding:clamp(24px,5vw,48px)}.range-card span{color:#6ee7d8;font-size:.8rem;font-weight:900;letter-spacing:.16em;text-transform:uppercase}.range-card strong{font:700 clamp(2.2rem,6vw,5rem)/1 Georgia,serif}.range-card p{color:#b6c9c4;line-height:1.7;max-width:760px}.no-recurring{border-top:1px solid #29433d;color:#6ee7d8;font-weight:800;margin-top:22px;padding-top:18px}@media(max-width:850px){.delivery{grid-template-columns:1fr}}
  `]
})
export class AymudoQuotePage implements OnInit {
  private readonly route=inject(ActivatedRoute);
  private readonly router=inject(Router);
  private readonly title=inject(Title);
  private readonly meta=inject(Meta);
  private readonly analytics=inject(AnalyticsService);
  protected readonly loading=signal(true);
  protected readonly error=signal('');
  protected readonly quote=signal<QuoteData|null>(null);
  protected readonly selectedPackage=signal('');
  protected readonly billingCycle=signal<'monthly'|'yearly'>('monthly');
  protected readonly stage=signal<'select'|'billing'|'done'>('select');
  protected readonly working=signal(false);
  protected readonly answersSaved=signal(false);
  protected readonly actionError=signal('');
  protected readonly purchaseReference=signal('');
  protected readonly successMessage=signal('');
  protected readonly declineOpen=signal(false);
  protected readonly declined=signal(false);
  protected answers:string[]=[];
  protected buyerName='';
  protected buyerEmail='';
  protected organization='';
  protected taxCountry='TR';
  protected taxNumber='';
  protected billingAddress='';
  protected paymentMethod:'credit_card'|'bank_transfer'='credit_card';
  protected declineReason='price';
  protected declineNote='';
  private token='';
  private purchaseToken='';

  ngOnInit():void{
    this.title.setTitle('Teklifin | Aymudo');
    this.meta.updateTag({name:'robots',content:'noindex,nofollow,noarchive'});
    this.token=this.route.snapshot.paramMap.get('token')||'';
    void this.load();
  }

  protected selected():QuotePackage|undefined{return this.quote()?.packages.find(item=>item.key===this.selectedPackage())}
  protected hasRecurring(data:QuoteData):boolean{return data.packages.some(item=>item.monthly>0)}
  protected dueNow():number{const item=this.selected();return item ? item.setup+(this.billingCycle()==='yearly'?item.yearly:item.monthly) : 0}
  protected money(value:number,currency:string):string{return new Intl.NumberFormat('tr-TR',{style:'currency',currency,maximumFractionDigits:0}).format(value||0)}
  protected dateText(value:string):string{return value ? `Geçerlilik: ${new Intl.DateTimeFormat('tr-TR',{dateStyle:'long'}).format(new Date(value.replace(' ','T')+'Z'))}` : ''}
  protected selectPackage(key:string):void{
    this.selectedPackage.set(key);
    this.analytics.track('quote.package',{entityTitle:key,metadata:{packageKey:key,billingCycle:this.billingCycle()}});
    setTimeout(()=>document.querySelector('#purchase')?.scrollIntoView({behavior:'smooth',block:'center'}));
  }

  protected async saveAnswers():Promise<void>{
    this.working.set(true);this.actionError.set('');
    try{
      await this.post('/api/aymudo/core/public-quote/answer',{token:this.token,answers:this.answers});
      await this.load();this.answersSaved.set(true);
    }
    catch(error){this.actionError.set(this.message(error))}
    finally{this.working.set(false)}
  }

  protected async decline(event:Event):Promise<void>{
    event.preventDefault();this.working.set(true);this.actionError.set('');
    try{
      await this.post('/api/aymudo/core/public-quote/decline',{token:this.token,reason:this.declineReason,note:this.declineNote});
      this.analytics.track('quote.declined',{entityTitle:this.declineReason,metadata:{reason:this.declineReason,hasNote:!!this.declineNote.trim()}});
      this.declined.set(true);this.declineOpen.set(false);
    }catch(error){this.actionError.set(this.message(error))}
    finally{this.working.set(false)}
  }

  protected async accept():Promise<void>{
    this.working.set(true);this.actionError.set('');
    try{
      const payload=await this.post('/api/aymudo/core/public-quote/accept',{token:this.token,packageKey:this.selectedPackage(),billingCycle:this.billingCycle()});
      const purchase=(payload['purchase']||{}) as Record<string,unknown>;
      this.purchaseToken=String(purchase['purchaseToken']||'');
      this.analytics.track('quote.accepted',{entityTitle:this.selectedPackage(),metadata:{packageKey:this.selectedPackage(),billingCycle:this.billingCycle(),dueNow:this.dueNow()}});
      const productUrl=String(purchase['productUrl']||'');
      if(productUrl){await this.router.navigateByUrl(productUrl);return;}
      const data=this.quote();this.buyerName=String(data?.customer.name||'');this.organization=String(data?.customer.organization||'');this.taxCountry=String(data?.country||'TR');
      this.stage.set('billing');
    }catch(error){this.actionError.set(this.message(error))}
    finally{this.working.set(false)}
  }

  protected async purchase(event:Event):Promise<void>{
    event.preventDefault();this.working.set(true);this.actionError.set('');
    try{
      const payload=await this.post('/api/aymudo/core/public-quote/purchase',{
        purchaseToken:this.purchaseToken,buyerName:this.buyerName,buyerEmail:this.buyerEmail,
        organization:this.organization,taxCountry:this.taxCountry.toUpperCase(),taxNumber:this.taxNumber,
        billingAddress:this.billingAddress,paymentMethod:this.paymentMethod
      });
      const purchase=(payload['purchase']||{}) as Record<string,unknown>;
      this.purchaseReference.set(String(purchase['reference']||''));
      this.analytics.track('quote.purchase',{entityTitle:String(purchase['reference']||''),metadata:{packageKey:this.selectedPackage(),billingCycle:this.billingCycle(),paymentMethod:this.paymentMethod,dueNow:this.dueNow()}});
      this.successMessage.set(String(payload['message']||'Satın alma kaydın oluşturuldu.'));
      this.stage.set('done');window.scrollTo({top:0,behavior:'smooth'});
    }catch(error){this.actionError.set(this.message(error))}
    finally{this.working.set(false)}
  }

  private async load():Promise<void>{
    try{
      const payload=await this.post('/api/aymudo/core/public-quote',{token:this.token});
      const quote=payload['quote'] as QuoteData;
      this.quote.set(quote);this.answers=quote.questions.map(()=>'');
      this.selectedPackage.set(quote.purchase?.packageKey||quote.recommendedPackage||quote.packages.find(item=>item.recommended)?.key||'');
      this.billingCycle.set(quote.purchase?.billingCycle||'monthly');
      this.buyerName=String(quote.customer.name||'');this.organization=String(quote.customer.organization||'');this.taxCountry=quote.country||'TR';
      if(quote.purchase){
        this.purchaseToken=quote.purchase.purchaseToken;
        this.buyerName=quote.purchase.buyerName||this.buyerName;
        this.buyerEmail=quote.purchase.buyerEmail||'';
        this.organization=quote.purchase.organization||this.organization;
        this.taxCountry=quote.purchase.taxCountry||this.taxCountry;
        this.taxNumber=quote.purchase.taxNumber||'';
        this.billingAddress=quote.purchase.billingAddress||'';
        this.paymentMethod=quote.purchase.paymentMethod||'credit_card';
        if(quote.purchase.status==='accepted'){
          this.stage.set('billing');
        }else{
          this.purchaseReference.set(quote.purchase.reference);
          this.successMessage.set(quote.purchase.status==='paid'
            ? 'Ödemen alındı. Proje başlangıç planın hazırlanıyor.'
            : 'Satın alma kaydın alındı. Ödeme hazırlığı e-posta adresin üzerinden devam ediyor.');
          this.stage.set('done');
        }
      }
    }catch(error){this.error.set(this.message(error))}
    finally{this.loading.set(false)}
  }

  private async post(url:string,body:Record<string,unknown>):Promise<Record<string,unknown>>{
    const response=await fetch(url,{method:'POST',headers:{'Content-Type':'application/json','X-Aymudo-Frontend':'next'},body:JSON.stringify(body)});
    const envelope=await response.json() as Record<string,unknown>;
    const payload=((envelope['data']||envelope) as Record<string,unknown>);
    if(!response.ok||envelope['success']===false||envelope['result']===false||payload['result']===false)throw new Error(String(payload['message']||envelope['message']||'İşlem tamamlanamadı.'));
    return payload;
  }
  private message(error:unknown):string{return error instanceof Error?error.message:'İşlem tamamlanamadı.'}
}
